import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Plus, Upload, X, Ban, RotateCcw, Pencil, MessageSquare, ChevronRight, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { api, downloadBlob } from '@/api/client';
import { MultiSelectFilter } from '@/components/ui/MultiSelectFilter';
import { useAssignableUsers } from '@/components/merchant/OwnerSelect';
import { useAuth } from '@/hooks/useAuth';
import { parseCsv } from '@/utils/csv-helper';
import { GmailPagination } from '@/components/ui/GmailPagination';
import { SearchableSelect } from '@/components/ui/SearchableSelect';

type LedgerEntry = {
  id: string;
  date: string;
  type: string;
  typeLabel: string;
  refNo: string;
  particulars: string;
  merchantId: number;
  merchantName: string;
  gstin: string;
  orderType: 'B2C' | 'B2B' | null;
  debit: number;
  credit: number;
  balance?: number;
  invoiceNo?: string | null;
  dueDate?: string | null;
  ageDays?: number;
  overdueDays?: number;
  remarkCount?: number;
  /** Sales person who owns this client (Merchant.salesUserId), resolved to a name by the API. */
  salesUserName?: string | null;
  nextFollowupDate?: string | null;
  cancelled?: boolean;
  cancelledAt?: string;
  cancelReason?: string;
};

type LedgerResponse = {
  items: LedgerEntry[];
  total: number;
  page: number;
  pageSize: number;
  merchantScoped: boolean;
  summary: { openingBalance: number; totalDebit: number; totalCredit: number; totalReceived: number; closingBalance: number };
  range: { from: string; to: string };
};

type GstRow = { gstin: string; clients: string[]; totalDebit: number; totalCredit: number; balance: number };
type GstReport = { rows: GstRow[]; range: { from: string; to: string }; totals: { totalDebit: number; totalCredit: number; balance: number } };

/** One client's closing balance for the period — the Sundry Debtors summary. */
type SummaryRow = {
  merchantId: number;
  merchantName: string;
  gstin: string;
  /** Set when this company is one of several belonging to the same owner (EGCPL, KIPS). */
  accountGroupId: number | null;
  accountGroupName: string | null;
  /** Sales person who owns this client (Merchant.salesUserId), resolved to a name by the API. */
  salesUserId: number | null;
  salesUserName: string | null;
  openingBalance: number;
  totalDebit: number;
  totalCredit: number;
  closingBalance: number;
  closingDebit: number;
  closingCredit: number;
};
type SummaryGroupTotal = {
  accountGroupId: number;
  accountGroupName: string;
  memberCount: number;
  openingBalance: number;
  totalDebit: number;
  totalCredit: number;
  closingDebit: number;
  closingCredit: number;
  closingBalance: number;
};
type SummaryReport = {
  rows: SummaryRow[];
  /** Only groups with 2+ companies on this sheet — a one-member subtotal just repeats its row. */
  groupTotals: SummaryGroupTotal[];
  range: { from: string; to: string };
  totals: { openingBalance: number; totalDebit: number; totalCredit: number; closingDebit: number; closingCredit: number; closingBalance: number };
};

/** One client's money split by how old it is — the leaf of the user-wise ageing report. */
type AgingClient = {
  merchantId: number;
  merchantName: string;
  gstin: string;
  salesUserId: number | null;
  salesUserName: string | null;
  /** One figure per bucket, in the order `buckets` gives them. */
  debit: number[];
  credit: number[];
  debitTotal: number;
  creditTotal: number;
  net: number;
  oldestDays: number;
};
type AgingUser = {
  salesUserId: number | null;
  salesUserName: string;
  clientCount: number;
  debit: number[];
  credit: number[];
  debitTotal: number;
  creditTotal: number;
  net: number;
  oldestDays: number;
  clients: AgingClient[];
};
type AgingReport = {
  buckets: { key: string; label: string }[];
  /** The cut-off the report was actually built to — the To date, clamped to today. */
  asAt: string;
  range: { from: string; to: string };
  rows: AgingUser[];
  totals: { clientCount: number; debit: number[]; credit: number[]; debitTotal: number; creditTotal: number; net: number };
};

const money = (n: number) => Number(n ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
/** Blank instead of 0.00 inside the ageing grid — a wall of zeros hides the buckets that hold money. */
const money0 = (n: number) => (n ? money(n) : '');

function monthStart() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}
function monthEnd() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10);
}

const TYPE_BADGE: Record<string, string> = {
  INVOICE: 'bg-blue-50 text-blue-700 border-blue-200',
  CREDIT_NOTE: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  DEBIT_NOTE: 'bg-amber-50 text-amber-700 border-amber-200',
  REMITTANCE: 'bg-violet-50 text-violet-700 border-violet-200',
  PAYMENT: 'bg-teal-50 text-teal-700 border-teal-200',
};

const PIE_COLORS = ['#0ea5e9', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#14b8a6', '#ec4899', '#6366f1', '#84cc16', '#f97316'];

export function LedgerPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isMerchant = user?.kind === 'merchant';

  const [view, setView] = useState<'ledger' | 'summary' | 'aging' | 'gst'>('ledger');
  const [importOpen, setImportOpen] = useState(false);
  const [entryOpen, setEntryOpen] = useState(false);
  const [editEntry, setEditEntry] = useState<LedgerEntry | null>(null);
  const [remarksEntry, setRemarksEntry] = useState<LedgerEntry | null>(null);
  // Every filter is multi-select. Ops reconcile "these four clients" or "invoices AND debit notes"
  // in one pass; forcing one value at a time meant running the same report N times and adding the
  // totals by hand, which is exactly where the numbers used to go wrong.
  const [merchantFilter, setMerchantFilter] = useState<(string | number)[]>([]);
  const [typeFilter, setTypeFilter] = useState<(string | number)[]>([]);
  const [orderTypeFilter, setOrderTypeFilter] = useState<(string | number)[]>([]);
  const [salesFilter, setSalesFilter] = useState<(string | number)[]>([]);
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState(monthStart());
  const [toDate, setToDate] = useState(monthEnd());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const { data: assignableUsers } = useAssignableUsers();

  const baseParams = useMemo(() => {
    const p: any = {};
    // Sent comma-joined; the API accepts that, a repeated param, or a single value.
    if (merchantFilter.length) p.merchantIds = merchantFilter.join(',');
    if (typeFilter.length) p.types = typeFilter.join(',');
    if (orderTypeFilter.length) p.orderTypes = orderTypeFilter.join(',');
    if (salesFilter.length) p.salesUserIds = salesFilter.join(',');
    if (search) p.q = search;
    if (fromDate) p.fromDate = fromDate;
    if (toDate) p.toDate = toDate;
    return p;
  }, [merchantFilter, typeFilter, orderTypeFilter, salesFilter, search, fromDate, toDate]);

  const params = useMemo(() => ({ ...baseParams, page, pageSize }), [baseParams, page, pageSize]);

  const { data, isLoading } = useQuery<LedgerResponse>({
    queryKey: ['ledger', params],
    queryFn: async () => (await api.get<LedgerResponse>('/ledger', { params })).data,
    enabled: view === 'ledger',
  });

  const { data: gst, isLoading: gstLoading } = useQuery<GstReport>({
    queryKey: ['ledger-gst', baseParams],
    queryFn: async () => (await api.get<GstReport>('/ledger/gst-report', { params: baseParams })).data,
    enabled: view === 'gst',
  });

  // Both dates are REQUIRED for the summary: it prints a CLOSING BALANCE, and a closing balance
  // without a cut-off date is not a number — it is whatever the server happened to default to. They
  // are pre-filled with the current month so the tab opens showing something, and the query simply
  // does not run if the operator clears one.
  const summaryDatesReady = !!fromDate && !!toDate && fromDate <= toDate;
  const { data: summaryReport, isLoading: summaryLoading } = useQuery<SummaryReport>({
    queryKey: ['ledger-summary', baseParams],
    queryFn: async () => (await api.get<SummaryReport>('/ledger/summary', { params: baseParams })).data,
    enabled: view === 'summary' && summaryDatesReady,
  });

  // Ageing is an AS-AT report: the To date is the cut-off, the From date is not applied (see the
  // service). Only the To date is required, so it runs as soon as there is one.
  const { data: agingReport, isLoading: agingLoading } = useQuery<AgingReport>({
    queryKey: ['ledger-aging', baseParams],
    queryFn: async () => (await api.get<AgingReport>('/ledger/aging', { params: baseParams })).data,
    enabled: view === 'aging' && !isMerchant && !!toDate,
  });

  const { data: merchants } = useQuery({
    queryKey: ['merchants-light-ledger'],
    queryFn: async () => (await api.get<any>('/merchants', { params: { pageSize: 500 } })).data,
    enabled: !isMerchant,
  });
  const merchantList = useMemo(
    () => (Array.isArray(merchants) ? merchants : (merchants as any)?.items ?? []),
    [merchants]
  );

  const items = data?.items ?? [];
  const summary = data?.summary;
  const scoped = data?.merchantScoped;

  const cancelMut = useMutation({
    mutationFn: async (e: LedgerEntry) =>
      (await api.post('/ledger/cancel', {
        entryId: e.id, merchantId: e.merchantId, debit: e.debit, credit: e.credit, entryDate: e.date,
      })).data,
    onSuccess: () => { toast.success('Entry cancelled'); qc.invalidateQueries({ queryKey: ['ledger'] }); },
    onError: (er: any) => toast.error(er?.response?.data?.message ?? 'Could not cancel entry'),
  });
  const restoreMut = useMutation({
    mutationFn: async (e: LedgerEntry) => (await api.post('/ledger/uncancel', { entryId: e.id })).data,
    onSuccess: () => { toast.success('Entry restored'); qc.invalidateQueries({ queryKey: ['ledger'] }); },
    onError: (er: any) => toast.error(er?.response?.data?.message ?? 'Could not restore entry'),
  });

  const followupMut = useMutation({
    mutationFn: async (v: { e: LedgerEntry; date: string }) =>
      (await api.post('/ledger/followup', { entryId: v.e.id, merchantId: v.e.merchantId, date: v.date || null })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ledger'] }); },
    onError: (er: any) => toast.error(er?.response?.data?.message ?? 'Could not save follow-up date'),
  });

  const handleExport = async () => {
    const qs = new URLSearchParams();
    Object.entries(baseParams).forEach(([k, v]) => qs.set(k, String(v)));
    // The button exports WHAT IS ON SCREEN. Downloading the transaction list while looking at the
    // summary is the kind of mismatch that gets a wrong sheet emailed to a client.
    if (view === 'summary') {
      await downloadBlob(`/ledger/summary/export?${qs.toString()}`, `ledger-summary-${fromDate}-to-${toDate}.csv`);
      return;
    }
    if (view === 'aging') {
      await downloadBlob(`/ledger/aging/export?${qs.toString()}`, `ledger-ageing-user-wise-as-at-${agingReport?.asAt ?? toDate}.csv`);
      return;
    }
    await downloadBlob(`/ledger/export?${qs.toString()}`, `ledger-${fromDate}-to-${toDate}.csv`);
  };

  const resetPage = () => setPage(1);
  // Sale Person shows for internal staff only — a merchant has no use for who owns their account.
  const colCount = 12 + (scoped ? 0 : 1) + (isMerchant ? 0 : 2); // +invoiceno,+duedate,+followup,+age; +client, +saleperson, +action

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold">Client Ledger</h1>
          <p className="text-xs text-gray-500 mt-0.5">
            Invoices, credit/debit notes, remittances and payments per client. Showing {data?.range?.from ?? summaryReport?.range?.from ?? gst?.range?.from} to {data?.range?.to ?? summaryReport?.range?.to ?? gst?.range?.to}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!isMerchant && (
            <button onClick={() => setEntryOpen(true)} className="px-3 py-2 bg-emerald-600 text-white rounded text-sm inline-flex items-center gap-1 hover:bg-emerald-700">
              <Plus size={14} /> Add Entry
            </button>
          )}
          {!isMerchant && (
            <button onClick={() => setImportOpen(true)} className="px-3 py-2 border border-emerald-600 text-emerald-700 rounded text-sm inline-flex items-center gap-1">
              <Upload size={14} /> Import Entries
            </button>
          )}
          <button onClick={handleExport} className="px-3 py-2 border border-emerald-600 text-emerald-700 rounded text-sm inline-flex items-center gap-1">
            <Download size={14} /> {view === 'summary' ? 'Export Summary' : view === 'aging' ? 'Export Ageing' : 'Export Ledger'}
          </button>
        </div>
      </div>

      {/* View tabs */}
      <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 mb-4 w-fit">
        {/* Ageing is grouped by the staff member who owns the client, so it is internal-only —
            a merchant's own statement grouped by our sales person tells them nothing. */}
        {(isMerchant ? (['ledger', 'summary', 'gst'] as const) : (['ledger', 'summary', 'aging', 'gst'] as const)).map((t) => (
          <button key={t} onClick={() => setView(t)}
            className={`px-5 py-1.5 text-sm rounded-md transition ${view === t ? 'bg-white shadow-sm text-emerald-700 font-medium' : 'text-slate-600 hover:text-slate-900'}`}>
            {t === 'ledger' ? 'Ledger' : t === 'summary' ? 'Summary' : t === 'aging' ? 'User-wise Ageing' : 'GST-wise Report'}
          </button>
        ))}
      </div>

      {/* Summary cards (ledger view only) */}
      {view === 'ledger' && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <SummaryCard label="Opening Balance" value={summary?.openingBalance} note={scoped ? undefined : 'Select a client'} />
          <SummaryCard label="Total Debit" value={summary?.totalDebit} tone="debit" />
          <SummaryCard label="Total Credit" value={summary?.totalCredit} tone="credit" />
          <SummaryCard label="Received from Merchant" value={summary?.totalReceived} tone="credit" />
          <SummaryCard label="Closing Balance" value={summary?.closingBalance} tone="balance" />
        </div>
      )}

      {/* Filters (shared by both views) */}
      <div className="grid grid-cols-12 gap-3 mb-4">
        {!isMerchant && (
          <div className="col-span-3">
            <label className="text-xs text-gray-500">Client (Merchant)</label>
            <MultiSelectFilter label="" placeholder="All Clients" values={merchantFilter}
              onChange={(v) => { setMerchantFilter(v); resetPage(); }}
              options={merchantList.map((m: any) => ({ value: m.id, label: `#${m.id} · ${m.companyName}`, hint: m.gstin ?? undefined }))} />
          </div>
        )}
        {!isMerchant && (
          <div className="col-span-2">
            <label className="text-xs text-gray-500">Sale Person</label>
            <MultiSelectFilter label="" placeholder="All Sale Persons" values={salesFilter}
              onChange={(v) => { setSalesFilter(v); resetPage(); }}
              options={[{ value: '-1', label: '— Unassigned —' }, ...(assignableUsers ?? []).map((u: any) => ({ value: u.id, label: u.name }))]} />
          </div>
        )}
        <div className="col-span-2">
          <label className="text-xs text-gray-500">Type</label>
          <MultiSelectFilter label="" placeholder="All Types" values={typeFilter} searchable={false}
            onChange={(v) => { setTypeFilter(v); resetPage(); }}
            options={[
              { value: 'INVOICE', label: 'Invoice' },
              { value: 'CREDIT_NOTE', label: 'Credit Note' },
              { value: 'DEBIT_NOTE', label: 'Debit Note' },
              // Remittance removed from the ledger 2026-08-10 — it is its own module.
              { value: 'PAYMENT', label: 'Payment Received' },
            ]} />
        </div>
        <div className="col-span-2">
          <label className="text-xs text-gray-500">Service</label>
          <MultiSelectFilter label="" placeholder="All Services" values={orderTypeFilter} searchable={false}
            onChange={(v) => { setOrderTypeFilter(v); resetPage(); }}
            options={[{ value: 'B2C', label: 'B2C' }, { value: 'B2B', label: 'B2B' }]} />
        </div>
        <div className="col-span-2">
          <label className="text-xs text-gray-500">Search</label>
          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); resetPage(); }}
            placeholder="Ref / particulars / GST"
            className="w-full border rounded px-2 py-1 text-sm"
          />
        </div>
        <div className="col-span-3">
          <label className="text-xs text-gray-500">Date Range</label>
          <div className="flex gap-1">
            <input type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); resetPage(); }} className="flex-1 border rounded px-2 py-1 text-sm" />
            <input type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); resetPage(); }} className="flex-1 border rounded px-2 py-1 text-sm" />
          </div>
        </div>
      </div>

      {view === 'ledger' ? (
        <>
          {!scoped && !isMerchant && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-3">
              Select a specific client to see a running balance. Across all clients, only debit/credit totals are shown.
            </div>
          )}

          {/* Results toolbar — entry count + per-page selector (visible at the top of the list) */}
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs text-gray-500">{(data?.total ?? 0).toLocaleString('en-IN')} entr{(data?.total ?? 0) === 1 ? 'y' : 'ies'}</div>
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <span>Per page:</span>
              <select
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); resetPage(); }}
                className="border rounded px-1.5 py-0.5"
              >
                {[25, 50, 100, 200, 500].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </div>

          <div className="overflow-x-auto bg-white border rounded">
            <table className="min-w-full text-sm">
              <thead className="bg-emerald-50">
                <tr>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-left">Ref No.</th>
                  <th className="px-3 py-2 text-left">Invoice No</th>
                  {!scoped && <th className="px-3 py-2 text-left">Client</th>}
                  {!isMerchant && <th className="px-3 py-2 text-left">Sale Person</th>}
                  <th className="px-3 py-2 text-left">Particulars</th>
                  <th className="px-3 py-2 text-left">GST No.</th>
                  <th className="px-3 py-2 text-left">Due Date</th>
                  <th className="px-3 py-2 text-left">Next Follow-up</th>
                  <th className="px-3 py-2 text-left">Age</th>
                  <th className="px-3 py-2 text-right">Debit</th>
                  <th className="px-3 py-2 text-right">Credit</th>
                  <th className="px-3 py-2 text-right">Balance</th>
                  {!isMerchant && <th className="px-3 py-2 text-center">Action</th>}
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan={colCount} className="text-center py-6 text-gray-400">Loading…</td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={colCount} className="text-center py-6 text-gray-400">No ledger entries for these filters.</td></tr>
                ) : (
                  items.map((e) => (
                    <tr key={e.id} className={`border-t ${e.cancelled ? 'bg-rose-50/40' : ''}`}>
                      <td className={`px-3 py-2 whitespace-nowrap ${e.cancelled ? 'line-through text-rose-400' : ''}`}>{e.date}</td>
                      <td className="px-3 py-2">
                        <span className={`border rounded-full px-2 py-0.5 text-xs ${e.cancelled ? 'bg-rose-50 text-rose-600 border-rose-200 line-through' : (TYPE_BADGE[e.type] ?? 'bg-gray-50 text-gray-600 border-gray-200')}`}>{e.typeLabel}</span>
                        {e.cancelled && <span className="ml-1 text-[10px] font-semibold text-rose-600">CANCELLED</span>}
                      </td>
                      <td className={`px-3 py-2 font-medium ${e.cancelled ? 'line-through text-rose-400' : ''}`}>{e.refNo}</td>
                      <td className={`px-3 py-2 font-mono text-xs ${e.cancelled ? 'line-through text-rose-400' : ''}`}>{e.invoiceNo || '—'}</td>
                      {!scoped && <td className={`px-3 py-2 ${e.cancelled ? 'line-through text-rose-400' : ''}`}>{e.merchantName || '—'}</td>}
                      {!isMerchant && (
                        <td className={`px-3 py-2 text-xs ${e.salesUserName ? 'text-gray-700' : 'text-gray-400 italic'} ${e.cancelled ? 'line-through' : ''}`}>
                          {e.salesUserName || 'Unassigned'}
                        </td>
                      )}
                      <td className={`px-3 py-2 text-gray-600 ${e.cancelled ? 'line-through text-rose-400' : ''}`}>{e.particulars}</td>
                      <td className={`px-3 py-2 font-mono text-xs ${e.cancelled ? 'line-through text-rose-400' : ''}`}>{e.gstin || '—'}</td>
                      <td className={`px-3 py-2 whitespace-nowrap ${e.cancelled ? 'line-through text-rose-400' : ''}`}>{e.dueDate || '—'}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {isMerchant ? (
                          <span className="text-gray-700">{e.nextFollowupDate || '—'}</span>
                        ) : (
                          <input
                            type="date"
                            value={e.nextFollowupDate ?? ''}
                            disabled={followupMut.isPending}
                            onChange={(ev) => followupMut.mutate({ e, date: ev.target.value })}
                            className="border rounded px-1.5 py-0.5 text-xs"
                            title="Set the next follow-up date for this entry (clear to remove)"
                          />
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap"><AgeCell ageDays={e.ageDays} overdueDays={e.overdueDays} cancelled={e.cancelled} /></td>
                      <td className={`px-3 py-2 text-right ${e.cancelled ? 'line-through text-rose-400' : ''}`}>{e.debit ? money(e.debit) : '—'}</td>
                      <td className={`px-3 py-2 text-right ${e.cancelled ? 'line-through text-rose-400' : ''}`}>{e.credit ? money(e.credit) : '—'}</td>
                      <td className="px-3 py-2 text-right font-medium">{e.balance !== undefined ? money(e.balance) : '—'}</td>
                      {!isMerchant && (
                        <td className="px-3 py-2 text-center whitespace-nowrap">
                          <div className="inline-flex items-center gap-1">
                            <button onClick={() => setRemarksEntry(e)} title="Remarks"
                              className="relative text-xs px-2 py-1 border border-slate-200 text-slate-600 rounded hover:bg-slate-50 inline-flex items-center gap-1">
                              <MessageSquare size={11} /> Remarks
                              {!!e.remarkCount && <span className="ml-0.5 bg-emerald-600 text-white rounded-full text-[10px] px-1.5 leading-4">{e.remarkCount}</span>}
                            </button>
                            {e.id.startsWith('LED-') && !e.cancelled && (
                              <button onClick={() => setEditEntry(e)} title="Edit entry"
                                className="text-xs px-2 py-1 border border-blue-200 text-blue-600 rounded hover:bg-blue-50 inline-flex items-center gap-1">
                                <Pencil size={11} /> Edit
                              </button>
                            )}
                            {e.cancelled ? (
                              <button onClick={() => restoreMut.mutate(e)} disabled={restoreMut.isPending}
                                className="text-xs px-2 py-1 border border-emerald-200 text-emerald-700 rounded hover:bg-emerald-50 inline-flex items-center gap-1">
                                <RotateCcw size={11} /> Restore
                              </button>
                            ) : (
                              <button onClick={() => { if (confirm(`Cancel this ${e.typeLabel} (${e.refNo})? It will stay listed but won't count towards the balance.`)) cancelMut.mutate(e); }} disabled={cancelMut.isPending}
                                className="text-xs px-2 py-1 border border-rose-200 text-rose-600 rounded hover:bg-rose-50 inline-flex items-center gap-1">
                                <Ban size={11} /> Cancel
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between mt-3">
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span>Rows:</span>
              <select
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); resetPage(); }}
                className="border rounded px-1 py-0.5"
              >
                {[25, 50, 100, 200, 500].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <GmailPagination page={page} pageSize={pageSize} total={data?.total ?? 0} onPage={setPage} />
          </div>
        </>
      ) : view === 'summary' ? (
        <LedgerSummaryView report={summaryReport} loading={summaryLoading} datesReady={summaryDatesReady} onExport={handleExport} showSalesPerson={!isMerchant} />
      ) : view === 'aging' ? (
        <LedgerAgingView report={agingReport} loading={agingLoading} onExport={handleExport} />
      ) : (
        <GstReportView report={gst} loading={gstLoading} />
      )}

      {importOpen && !isMerchant && (
        <ImportLedgerModal
          onClose={() => setImportOpen(false)}
          onDone={() => qc.invalidateQueries({ queryKey: ['ledger'] })}
        />
      )}

      {entryOpen && !isMerchant && (
        <AddLedgerEntryModal
          merchants={merchantList}
          onClose={() => setEntryOpen(false)}
          onDone={() => { qc.invalidateQueries({ queryKey: ['ledger'] }); qc.invalidateQueries({ queryKey: ['ledger-gst'] }); }}
        />
      )}

      {editEntry && !isMerchant && (
        <AddLedgerEntryModal
          merchants={merchantList}
          editEntry={editEntry}
          onClose={() => setEditEntry(null)}
          onDone={() => { qc.invalidateQueries({ queryKey: ['ledger'] }); qc.invalidateQueries({ queryKey: ['ledger-gst'] }); }}
        />
      )}

      {remarksEntry && (
        <RemarksModal
          entry={remarksEntry}
          canAdd={!isMerchant}
          onClose={() => setRemarksEntry(null)}
          onDone={() => qc.invalidateQueries({ queryKey: ['ledger'] })}
        />
      )}
    </div>
  );
}

function AgeCell({ ageDays, overdueDays, cancelled }: { ageDays?: number; overdueDays?: number; cancelled?: boolean }) {
  if (ageDays === undefined) return <span className="text-gray-400">—</span>;
  const overdue = (overdueDays ?? 0) > 0;
  return (
    <span className={`inline-flex flex-col leading-tight ${cancelled ? 'line-through text-rose-300' : ''}`}>
      <span className="text-gray-700">{ageDays}d old</span>
      {overdue && !cancelled && (
        <span className="text-[10px] font-semibold text-rose-600">{overdueDays}d overdue</span>
      )}
    </span>
  );
}

/**
 * Sundry Debtors summary — one line per client, showing what they owe at the end of the period.
 *
 * Laid out like the Tally sheet finance reconcile against: the period across the top, the closing
 * balance split into a Debit and a Credit column, and a Grand Total at the foot. The Opening /
 * Debit / Credit columns in between are what let a disputed closing figure be checked without
 * opening each client's statement one at a time — closing = opening + debit - credit, on the row.
 */
function LedgerSummaryView({ report, loading, datesReady, onExport, showSalesPerson }: { report?: SummaryReport; loading: boolean; datesReady: boolean; onExport: () => void; showSalesPerson: boolean }) {
  const rows = report?.rows ?? [];
  const totals = report?.totals;
  // Sale Person shows for internal staff only, exactly as in the ledger list — a merchant has no
  // use for who owns their account. Every colSpan below is driven off this so the group header,
  // the subtotal and the Grand Total keep spanning the full width when the column appears.
  const cols = showSalesPerson ? 8 : 7;
  const labelSpan = showSalesPerson ? 3 : 2;
  // Subtotals come from the server so the sheet and the CSV cannot disagree on them.
  const groupTotals = useMemo(
    () => new Map((report?.groupTotals ?? []).map((g) => [g.accountGroupId, g])),
    [report],
  );

  if (!datesReady) {
    return (
      <div className="bg-white border rounded p-8 text-center">
        <div className="text-sm font-medium text-slate-700">Pick a start and end date</div>
        <p className="text-xs text-gray-500 mt-1 max-w-md mx-auto">
          The summary shows each client&apos;s closing balance <em>as at</em> the end date, so both dates are
          needed. They default to the current month — set the From date on or before the To date.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white border rounded overflow-hidden">
      <div className="px-4 py-3 border-b bg-slate-50 flex flex-wrap items-baseline justify-between gap-2">
        <div className="text-sm font-semibold text-slate-800">
          Sundry Debtors <span className="font-normal text-slate-500">· Closing Balance</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-xs text-gray-600">
            {report?.range ? `${report.range.from} to ${report.range.to}` : ''} · {rows.length.toLocaleString('en-IN')} client{rows.length === 1 ? '' : 's'}
          </div>
          <button onClick={onExport} disabled={!rows.length}
            className="px-3 py-1.5 border border-emerald-600 text-emerald-700 rounded text-xs inline-flex items-center gap-1 hover:bg-emerald-50 disabled:opacity-40 disabled:cursor-not-allowed">
            <Download size={13} /> Export
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-emerald-50 text-slate-700">
            <tr>
              <th rowSpan={2} className="px-3 py-2 text-left align-bottom border-r">Particulars</th>
              {showSalesPerson && <th rowSpan={2} className="px-3 py-2 text-left align-bottom border-r">Sale Person</th>}
              <th rowSpan={2} className="px-3 py-2 text-left align-bottom border-r">GST No.</th>
              <th rowSpan={2} className="px-3 py-2 text-right align-bottom border-r">Opening Balance</th>
              <th colSpan={2} className="px-3 py-1.5 text-center border-r border-b">Transactions</th>
              <th colSpan={2} className="px-3 py-1.5 text-center border-b">Closing Balance</th>
            </tr>
            <tr>
              <th className="px-3 py-1.5 text-right">Debit</th>
              <th className="px-3 py-1.5 text-right border-r">Credit</th>
              <th className="px-3 py-1.5 text-right">Debit</th>
              <th className="px-3 py-1.5 text-right">Credit</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={cols} className="text-center py-6 text-gray-400">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={cols} className="text-center py-6 text-gray-400">No client had a balance or any activity in this period.</td></tr>
            ) : (
              <>
                {rows.map((r, i) => {
                  // `rows` arrives sorted with each owner's companies adjacent, so the group header
                  // goes in when the group CHANGES and the subtotal goes in when it ends. Both are
                  // driven off `groupTotals`, which only carries groups with 2+ companies present —
                  // a lone company from a group renders as an ordinary row with a quiet caption.
                  const prev = i > 0 ? rows[i - 1] : null;
                  const next = i + 1 < rows.length ? rows[i + 1] : null;
                  const g = r.accountGroupId != null ? groupTotals.get(r.accountGroupId) : undefined;
                  const opensGroup = !!g && prev?.accountGroupId !== r.accountGroupId;
                  const closesGroup = !!g && next?.accountGroupId !== r.accountGroupId;
                  return (
                    <Fragment key={r.merchantId}>
                      {opensGroup && (
                        <tr className="border-t bg-indigo-50/60">
                          <td colSpan={cols} className="px-3 py-1.5 text-xs font-semibold text-indigo-800">
                            {g!.accountGroupName}
                            <span className="font-normal text-indigo-600"> · one owner, {g!.memberCount} companies</span>
                          </td>
                        </tr>
                      )}
                      <tr className="border-t hover:bg-slate-50">
                        <td className={`px-3 py-2 border-r ${g ? 'pl-6' : ''}`}>
                          {r.merchantName}
                          {!g && r.accountGroupName && (
                            <span className="ml-2 text-[11px] text-indigo-600">({r.accountGroupName})</span>
                          )}
                        </td>
                        {showSalesPerson && (
                          <td className={`px-3 py-2 text-xs border-r ${r.salesUserName ? 'text-gray-700' : 'text-gray-400 italic'}`}>
                            {r.salesUserName || 'Unassigned'}
                          </td>
                        )}
                        <td className="px-3 py-2 font-mono text-xs text-gray-600 border-r">{r.gstin || '—'}</td>
                        <td className="px-3 py-2 text-right text-gray-600 border-r">{money(r.openingBalance)}</td>
                        <td className="px-3 py-2 text-right text-gray-600">{r.totalDebit ? money(r.totalDebit) : ''}</td>
                        <td className="px-3 py-2 text-right text-gray-600 border-r">{r.totalCredit ? money(r.totalCredit) : ''}</td>
                        {/* Debit always prints (0.00 when the balance sits on the credit side); Credit
                            stays blank unless it holds the balance — the same convention as the Tally
                            sheet this is checked against, so the two can be diffed column for column. */}
                        <td className="px-3 py-2 text-right font-medium text-blue-700">{money(r.closingDebit)}</td>
                        <td className="px-3 py-2 text-right font-medium text-emerald-700">{r.closingCredit ? money(r.closingCredit) : ''}</td>
                      </tr>
                      {closesGroup && (
                        <tr className="border-t bg-indigo-50/60 font-semibold text-indigo-900">
                          <td className="px-3 py-2 border-r" colSpan={labelSpan}>{g!.accountGroupName} — Group Total</td>
                          <td className="px-3 py-2 text-right border-r">{money(g!.openingBalance)}</td>
                          <td className="px-3 py-2 text-right">{money(g!.totalDebit)}</td>
                          <td className="px-3 py-2 text-right border-r">{money(g!.totalCredit)}</td>
                          <td className="px-3 py-2 text-right">{money(g!.closingDebit)}</td>
                          <td className="px-3 py-2 text-right">{g!.closingCredit ? money(g!.closingCredit) : ''}</td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
                <tr className="border-t-2 bg-slate-50 font-semibold">
                  <td className="px-3 py-2 border-r" colSpan={labelSpan}>Grand Total</td>
                  <td className="px-3 py-2 text-right border-r">{money(totals?.openingBalance ?? 0)}</td>
                  <td className="px-3 py-2 text-right">{money(totals?.totalDebit ?? 0)}</td>
                  <td className="px-3 py-2 text-right border-r">{money(totals?.totalCredit ?? 0)}</td>
                  <td className="px-3 py-2 text-right text-blue-700">{money(totals?.closingDebit ?? 0)}</td>
                  <td className="px-3 py-2 text-right text-emerald-700">{money(totals?.closingCredit ?? 0)}</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Ledger — User wise: how OLD the money is, under the sales person who owns the client.
 *
 * The Summary says how much a client owes; this says how long they have owed it and whose account
 * it is. Debit and credit are aged side by side, each split 0-30 / 31-60 / 61-90 / 90+, with the
 * client breakdown one click away — the person on the call needs the name of the account, not just
 * the total against their own name.
 *
 * The report is an AS-AT snapshot at the To date and deliberately ignores the From date; the header
 * says so, because a number that does not tie to the Summary has to explain itself on the screen it
 * is read from.
 */
function LedgerAgingView({ report, loading, onExport }: { report?: AgingReport; loading: boolean; onExport: () => void }) {
  const rows = report?.rows ?? [];
  const buckets = report?.buckets ?? [];
  const totals = report?.totals;
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const toggle = (k: string) => setOpen((o) => ({ ...o, [k]: !o[k] }));
  // 3 label columns + (buckets + total) twice + net.
  const cols = 3 + (buckets.length + 1) * 2 + 1;
  // The oldest bucket is the one that gets acted on, so it is the one that is coloured.
  const lastBucket = buckets.length - 1;

  const bucketCells = (vals: number[], total: number, tone: 'debit' | 'credit', strong = false) => (
    <>
      {vals.map((v, i) => (
        <td key={i} className={`px-3 py-2 text-right tabular-nums ${
          i === lastBucket && v ? 'text-rose-700 font-medium' : 'text-gray-600'
        } ${strong ? 'font-semibold' : ''}`}>
          {money0(v)}
        </td>
      ))}
      <td className={`px-3 py-2 text-right tabular-nums border-r font-medium ${tone === 'debit' ? 'text-blue-700' : 'text-emerald-700'}`}>
        {money(total)}
      </td>
    </>
  );

  return (
    <div className="bg-white border rounded overflow-hidden">
      <div className="px-4 py-3 border-b bg-slate-50 flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <div className="text-sm font-semibold text-slate-800">
            Ledger — User wise <span className="font-normal text-slate-500">· Ageing of the balance</span>
          </div>
          <div className="text-[11px] text-gray-500 mt-0.5">
            As at <strong>{report?.asAt ?? '—'}</strong> — every document up to that date, aged from its own date.
            The <em>From</em> date is not applied here: restricted to one month, nothing could ever reach the 90+ column.
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-xs text-gray-600">
            {rows.length} sale{rows.length === 1 ? '' : 's'} person · {totals?.clientCount ?? 0} client{totals?.clientCount === 1 ? '' : 's'}
          </div>
          <button onClick={onExport} disabled={!rows.length}
            className="px-3 py-1.5 border border-emerald-600 text-emerald-700 rounded text-xs inline-flex items-center gap-1 hover:bg-emerald-50 disabled:opacity-40 disabled:cursor-not-allowed">
            <Download size={13} /> Export
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-emerald-50 text-slate-700">
            <tr>
              <th rowSpan={2} className="px-3 py-2 text-left align-bottom border-r">Sale Person / Client</th>
              <th rowSpan={2} className="px-3 py-2 text-right align-bottom">Clients</th>
              <th rowSpan={2} className="px-3 py-2 text-right align-bottom border-r">Oldest</th>
              <th colSpan={buckets.length + 1} className="px-3 py-1.5 text-center border-b border-r">Debit (what they owe)</th>
              <th colSpan={buckets.length + 1} className="px-3 py-1.5 text-center border-b border-r">Credit (paid / adjusted)</th>
              <th rowSpan={2} className="px-3 py-2 text-right align-bottom">Net Outstanding</th>
            </tr>
            <tr>
              {buckets.map((b) => <th key={`d-${b.key}`} className="px-3 py-1.5 text-right font-medium">{b.label}</th>)}
              <th className="px-3 py-1.5 text-right border-r">Total</th>
              {buckets.map((b) => <th key={`c-${b.key}`} className="px-3 py-1.5 text-right font-medium">{b.label}</th>)}
              <th className="px-3 py-1.5 text-right border-r">Total</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={cols} className="text-center py-6 text-gray-400">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={cols} className="text-center py-6 text-gray-400">Nothing outstanding as at this date.</td></tr>
            ) : (
              <>
                {rows.map((u) => {
                  const key = String(u.salesUserId ?? 0);
                  const isOpen = !!open[key];
                  return (
                    <Fragment key={key}>
                      <tr className="border-t hover:bg-slate-50 cursor-pointer" onClick={() => toggle(key)}>
                        <td className="px-3 py-2 border-r font-medium text-slate-800">
                          <span className="inline-flex items-center gap-1">
                            {isOpen ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
                            <span className={u.salesUserId ? '' : 'text-gray-400 italic'}>{u.salesUserName}</span>
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right text-gray-500 tabular-nums">{u.clientCount}</td>
                        <td className="px-3 py-2 text-right border-r tabular-nums">
                          <span className={u.oldestDays > 90 ? 'text-rose-700 font-medium' : 'text-gray-500'}>{u.oldestDays ? `${u.oldestDays}d` : '—'}</span>
                        </td>
                        {bucketCells(u.debit, u.debitTotal, 'debit', true)}
                        {bucketCells(u.credit, u.creditTotal, 'credit', true)}
                        <td className="px-3 py-2 text-right font-semibold tabular-nums text-slate-800">{money(u.net)}</td>
                      </tr>
                      {isOpen && u.clients.map((c) => (
                        <tr key={c.merchantId} className="border-t bg-slate-50/60 text-[13px]">
                          <td className="px-3 py-1.5 border-r pl-9 text-slate-700">
                            {c.merchantName}
                            {c.gstin && <span className="ml-2 font-mono text-[10px] text-gray-400">{c.gstin}</span>}
                          </td>
                          <td className="px-3 py-1.5"></td>
                          <td className="px-3 py-1.5 text-right border-r tabular-nums">
                            <span className={c.oldestDays > 90 ? 'text-rose-600' : 'text-gray-400'}>{c.oldestDays ? `${c.oldestDays}d` : '—'}</span>
                          </td>
                          {bucketCells(c.debit, c.debitTotal, 'debit')}
                          {bucketCells(c.credit, c.creditTotal, 'credit')}
                          <td className="px-3 py-1.5 text-right tabular-nums text-slate-700">{money(c.net)}</td>
                        </tr>
                      ))}
                    </Fragment>
                  );
                })}
                <tr className="border-t-2 bg-slate-50 font-semibold">
                  <td className="px-3 py-2 border-r">Grand Total</td>
                  <td className="px-3 py-2 text-right tabular-nums">{totals?.clientCount ?? 0}</td>
                  <td className="px-3 py-2 border-r"></td>
                  {bucketCells(totals?.debit ?? [], totals?.debitTotal ?? 0, 'debit', true)}
                  {bucketCells(totals?.credit ?? [], totals?.creditTotal ?? 0, 'credit', true)}
                  <td className="px-3 py-2 text-right tabular-nums">{money(totals?.net ?? 0)}</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2 border-t bg-white text-[11px] text-gray-500">
        Ageing runs off each document&apos;s own date. Payments and credit notes are aged the same way and shown
        separately — they are not matched off against a particular invoice, so <strong>Net Outstanding</strong> is the
        client&apos;s balance as at {report?.asAt ?? 'the cut-off'}, and it ties back to the Summary tab.
      </div>
    </div>
  );
}

function GstReportView({ report, loading }: { report?: GstReport; loading: boolean }) {
  const rows = report?.rows ?? [];
  const pieData = useMemo(
    () => rows.filter((r) => Math.abs(r.balance) > 0.001).map((r) => ({ name: r.gstin, value: Math.abs(r.balance), signed: r.balance })),
    [rows]
  );
  return (
    <div className="grid grid-cols-12 gap-4">
      <div className="col-span-12 lg:col-span-7 overflow-x-auto bg-white border rounded">
        <table className="min-w-full text-sm">
          <thead className="bg-emerald-50">
            <tr>
              <th className="px-3 py-2 text-left">GST No.</th>
              <th className="px-3 py-2 text-left">Clients</th>
              <th className="px-3 py-2 text-right">Total Debit</th>
              <th className="px-3 py-2 text-right">Total Credit</th>
              <th className="px-3 py-2 text-right">Balance</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="text-center py-6 text-gray-400">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-6 text-gray-400">No data for these filters.</td></tr>
            ) : (
              <>
                {rows.map((r) => (
                  <tr key={r.gstin} className="border-t">
                    <td className="px-3 py-2 font-mono text-xs">{r.gstin}</td>
                    <td className="px-3 py-2 text-gray-600 text-xs">{r.clients.join(', ') || '—'}</td>
                    <td className="px-3 py-2 text-right">{money(r.totalDebit)}</td>
                    <td className="px-3 py-2 text-right">{money(r.totalCredit)}</td>
                    <td className={`px-3 py-2 text-right font-semibold ${r.balance >= 0 ? 'text-blue-700' : 'text-emerald-700'}`}>{money(r.balance)}</td>
                  </tr>
                ))}
                <tr className="border-t-2 bg-slate-50 font-semibold">
                  <td className="px-3 py-2" colSpan={2}>Total</td>
                  <td className="px-3 py-2 text-right">{money(report?.totals.totalDebit ?? 0)}</td>
                  <td className="px-3 py-2 text-right">{money(report?.totals.totalCredit ?? 0)}</td>
                  <td className="px-3 py-2 text-right">{money(report?.totals.balance ?? 0)}</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
      <div className="col-span-12 lg:col-span-5 bg-white border rounded p-4">
        <div className="text-sm font-medium text-slate-700 mb-2">Balance by GST No.</div>
        {pieData.length === 0 ? (
          <div className="text-center text-gray-400 py-16 text-sm">No outstanding balance to chart.</div>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={110} label={(e: any) => e.name?.slice(0, 6)}>
                {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v: any, _n: any, p: any) => [`₹ ${money(p?.payload?.signed ?? v)}`, p?.payload?.name]} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

const TXN_TYPE_OPTIONS = [
  { value: 'INVOICE', label: 'Invoice (debit)' },
  { value: 'DEBIT_NOTE', label: 'Debit Note (debit)' },
  { value: 'CREDIT_NOTE', label: 'Credit Note (credit)' },
  // No Remittance option: the ledger no longer carries remittances at all, and the API now rejects
  // the type, so offering it here would only produce a save that fails.
  { value: 'PAYMENT', label: 'Received payment from merchant (credit)' },
];

// Local datetime string (yyyy-MM-ddTHH:mm) for a datetime-local default of "now".
function nowLocalInput() {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
// Convert an ISO/date value to a datetime-local input value.
function toLocalInput(v: any) {
  if (!v) return nowLocalInput();
  const d = new Date(v);
  if (isNaN(d.getTime())) return nowLocalInput();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function AddLedgerEntryModal({ merchants, editEntry, onClose, onDone }: { merchants: any[]; editEntry?: LedgerEntry | null; onClose: () => void; onDone: () => void }) {
  const isEdit = !!editEntry;
  const [merchantId, setMerchantId] = useState<number | ''>('');
  const [txnType, setTxnType] = useState('INVOICE');
  const [orderType, setOrderType] = useState('');
  const [documentNo, setDocumentNo] = useState('');
  const [gstin, setGstin] = useState('');
  const [amount, setAmount] = useState('');
  const [narration, setNarration] = useState('');
  const [entryDate, setEntryDate] = useState(nowLocalInput());
  const [invoiceNo, setInvoiceNo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const isPayment = txnType === 'PAYMENT';

  // In edit mode, load the raw manual entry and prefill the form.
  const { data: raw, isLoading: loadingRaw } = useQuery({
    queryKey: ['ledger-entry', editEntry?.id],
    queryFn: async () => (await api.get(`/ledger/entry/${editEntry!.id}`)).data,
    enabled: isEdit,
  });
  useEffect(() => {
    if (!raw) return;
    setMerchantId(raw.merchantId ?? '');
    setTxnType(raw.txnType ?? 'INVOICE');
    setOrderType(raw.orderType ?? '');
    setDocumentNo(raw.documentNo ?? '');
    setGstin(raw.gstin ?? '');
    setAmount(String(raw.amount ?? ''));
    setNarration(raw.narration ?? '');
    setEntryDate(toLocalInput(raw.entryDate));
    setInvoiceNo(raw.invoiceNo ?? '');
  }, [raw]);

  const selectedMerchant = merchants.find((m: any) => m.id === Number(merchantId));

  // OUR company GST numbers (seller GSTINs) — the GST dropdown options.
  const { data: ourGst = [] } = useQuery<{ gstin: string; name: string }[]>({
    queryKey: ['ledger-our-gst'],
    queryFn: async () => {
      const r: any = (await api.get('/settings/invoice_companies')).data;
      const arr = Array.isArray(r) ? r : (r?.items ?? []);
      return (arr as any[]).filter((c) => c && (c.company_gstin || c.gstin) && c.isActive !== false)
        .map((c) => ({ gstin: c.company_gstin || c.gstin, name: c.company_name || c.name || '' }));
    },
  });

  // Invoice numbers available for the selected merchant (Invoices + ledger) — for Payment Received.
  const { data: invoiceOpts = [] } = useQuery<string[]>({
    queryKey: ['ledger-inv-opts', merchantId],
    enabled: isPayment && !!merchantId,
    queryFn: async () => (await api.get<string[]>('/ledger/invoice-options', { params: { merchantId } })).data,
  });

  // GST No. is not relevant for money-in rows.
  const hideGst = isPayment || txnType === 'REMITTANCE';

  const pickMerchant = (v: string | number | '') => {
    setMerchantId(v === '' ? '' : Number(v));
  };

  const canSave = !!merchantId && !!documentNo.trim() && Number(amount) > 0 && (!isPayment || !!invoiceNo.trim());

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        merchantId,
        txnType,
        orderType: orderType || undefined,
        documentNo: documentNo.trim(),
        invoiceNo: invoiceNo.trim() || undefined,
        gstin: gstin.trim() || undefined,
        amount,
        narration: narration.trim() || undefined,
        entryDate: entryDate || undefined,
      };
      return isEdit
        ? (await api.post('/ledger/entry/update', { ...payload, id: editEntry!.id })).data
        : (await api.post('/ledger/entry', payload)).data;
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Ledger entry updated' : 'Ledger entry added');
      onDone();
      onClose();
    },
    onError: (e: any) => setError(e?.response?.data?.message ?? (isEdit ? 'Failed to update entry' : 'Failed to add entry')),
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-16 z-50">
      <div className="bg-white rounded-lg w-[560px] max-w-[95vw] max-h-[85vh] overflow-y-auto">
        <div className="bg-emerald-700 text-white px-5 py-3 flex items-center justify-between">
          <div className="font-semibold">{isEdit ? 'Edit Ledger Entry' : 'Add Ledger Entry'}</div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        {isEdit && loadingRaw && <div className="px-5 pt-4 text-xs text-gray-500">Loading entry…</div>}
        <div className="p-5 space-y-4 text-sm">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Client (Merchant) *</label>
            <SearchableSelect
              value={merchantId}
              onChange={pickMerchant}
              placeholder="Search & select client…"
              options={(merchants ?? []).map((m: any) => ({ value: m.id, label: `#${m.id} · ${m.companyName}`, hint: m.gstin ?? undefined }))}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Transaction Type *</label>
              <select value={txnType} onChange={(e) => setTxnType(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm">
                {TXN_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Service (optional)</label>
              <select value={orderType} onChange={(e) => setOrderType(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm">
                <option value="">—</option>
                <option value="B2C">B2C</option>
                <option value="B2B">B2B</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Document No / Invoice No *</label>
              <input value={documentNo} onChange={(e) => setDocumentNo(e.target.value)} placeholder="e.g. INV/B2B/202607/0001"
                className="w-full border rounded px-2 py-1.5 text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Invoice No {isPayment && <span className="text-rose-600">*</span>}</label>
              {isPayment ? (
                <select value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)}
                  className={`w-full border rounded px-2 py-1.5 text-sm ${!invoiceNo.trim() ? 'border-rose-300 bg-rose-50/40' : ''}`}>
                  <option value="">— select invoice —</option>
                  {invoiceOpts.map((n) => <option key={n} value={n}>{n}</option>)}
                  {invoiceNo && !invoiceOpts.includes(invoiceNo) && <option value={invoiceNo}>{invoiceNo}</option>}
                </select>
              ) : (
                <input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} placeholder="Optional"
                  className="w-full border rounded px-2 py-1.5 text-sm" />
              )}
              {isPayment && <div className="text-[11px] text-gray-400 mt-0.5">
                {!merchantId ? 'Select a client first.' : invoiceOpts.length === 0 ? 'No invoices found for this client (Invoices or ledger).' : 'Pick an invoice of the selected client.'}
              </div>}
            </div>
            {!hideGst && (
              <div>
                <label className="text-xs text-gray-500 block mb-1">GST No.</label>
                <select value={gstin} onChange={(e) => setGstin(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm font-mono">
                  <option value="">— select GST —</option>
                  {ourGst.map((c) => <option key={c.gstin} value={c.gstin}>{c.gstin}{c.name ? ` · ${c.name}` : ''}</option>)}
                  {gstin && !ourGst.some((c) => c.gstin === gstin) && <option value={gstin}>{gstin}</option>}
                </select>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Amount (₹) *</label>
              <input value={amount} onChange={(e) => setAmount(e.target.value)} type="number" min="0" step="0.01" placeholder="0.00"
                className="w-full border rounded px-2 py-1.5 text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Date &amp; Time</label>
              <input value={entryDate} onChange={(e) => setEntryDate(e.target.value)} type="datetime-local"
                className="w-full border rounded px-2 py-1.5 text-sm" />
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-500 block mb-1">Narration (optional)</label>
            <input value={narration} onChange={(e) => setNarration(e.target.value)} placeholder="Free-text note"
              className="w-full border rounded px-2 py-1.5 text-sm" />
          </div>

          <p className="text-xs text-gray-500">
            Invoice / Debit Note post as a <b>debit</b>; Credit Note / Remittance / Payment Received post as a <b>credit</b>. A
            Document No that already exists (as an invoice, credit/debit note or ledger entry) is <b>not accepted</b>.
            {selectedMerchant?.gstin && <> Client GST: <span className="font-mono">{selectedMerchant.gstin}</span>.</>}
          </p>

          {error && <div className="rounded border border-rose-300 bg-rose-50 text-rose-700 px-3 py-2 text-xs">{error}</div>}
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-3">
          <button onClick={onClose} className="border border-gray-300 text-gray-600 px-4 py-1.5 rounded text-sm">Cancel</button>
          <button
            onClick={() => { setError(null); save.mutate(); }}
            disabled={!canSave || save.isPending}
            className="bg-emerald-600 text-white px-5 py-1.5 rounded text-sm disabled:opacity-50"
          >
            {save.isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Entry'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ImportLedgerModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<any[]>([]);
  const [fileName, setFileName] = useState('');
  const [result, setResult] = useState<{ created: number; failed: number; errors: string[] } | null>(null);

  const onFile = (f: File | undefined) => {
    if (!f) return;
    setFileName(f.name);
    setResult(null);
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseCsv(String(reader.result ?? ''));
      setRows(parsed.rows);
    };
    reader.readAsText(f);
  };

  const runImport = useMutation({
    mutationFn: async () => (await api.post('/ledger/import', { rows })).data,
    onSuccess: (r) => { setResult(r); onDone(); },
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-16 z-50">
      <div className="bg-white rounded-lg w-[640px] max-h-[85vh] overflow-y-auto">
        <div className="bg-emerald-700 text-white px-5 py-3 flex items-center justify-between">
          <div className="font-semibold">Import Ledger Entries</div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4 text-sm">
          <p className="text-gray-600">
            Upload a CSV with these columns:
            <span className="block mt-1 font-mono text-xs bg-gray-50 border rounded p-2">
              Merchant ID, Transaction Type, Document No, GST No, Amount, Narration, Date and Time
            </span>
            Each row posts to the client named by its <b>Merchant ID</b> — one file can cover many clients.
            <b>Transaction Type</b> must be one of <b>INVOICE</b>, <b>CRNOTE</b>, <b>DBNOTE</b> or <b>PAYMENT</b>.
            Merchant ID, Transaction Type, Amount and Document No are <b>required</b>. INVOICE / DBNOTE post as a <b>debit</b>,
            CRNOTE / PAYMENT post as a <b>credit</b>. GST No is optional. Dates accept dd/mm/yyyy or dd/mm/yyyy hh:mm.
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => downloadBlob('/ledger/import-template', 'ledger-import-template.csv')}
              className="px-3 py-1.5 border border-emerald-600 text-emerald-700 rounded text-sm"
            >
              Download Sample CSV
            </button>
            <button
              onClick={() => fileRef.current?.click()}
              className="px-3 py-1.5 border rounded text-sm inline-flex items-center gap-1"
            >
              <Upload size={14} /> Choose CSV
            </button>
            <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
            {fileName && <span className="text-xs text-gray-500">{fileName} • {rows.length} row(s)</span>}
          </div>
          {result && (
            <div className={`rounded border p-3 text-xs ${result.failed ? 'border-amber-400 bg-amber-50' : 'border-emerald-400 bg-emerald-50'}`}>
              <div className="font-semibold">Imported {result.created} entr{result.created === 1 ? 'y' : 'ies'}{result.failed ? `, ${result.failed} failed` : ''}.</div>
              {result.errors?.length > 0 && (
                <ul className="mt-1 list-disc pl-4 text-amber-700 max-h-40 overflow-y-auto">
                  {result.errors.map((er, i) => <li key={i}>{er}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-3">
          <button onClick={onClose} className="border border-gray-300 text-gray-600 px-4 py-1.5 rounded text-sm">Close</button>
          <button
            onClick={() => runImport.mutate()}
            disabled={rows.length === 0 || runImport.isPending}
            className="bg-emerald-600 text-white px-5 py-1.5 rounded text-sm disabled:opacity-50"
          >
            {runImport.isPending ? 'Importing…' : `Import ${rows.length || ''} Entr${rows.length === 1 ? 'y' : 'ies'}`}
          </button>
        </div>
      </div>
    </div>
  );
}

type Remark = { id: number; remark: string; by: string; at: string };

function RemarksModal({ entry, canAdd, onClose, onDone }: { entry: LedgerEntry; canAdd: boolean; onClose: () => void; onDone: () => void }) {
  const qc = useQueryClient();
  const [text, setText] = useState('');

  const { data: remarks, isLoading } = useQuery<Remark[]>({
    queryKey: ['ledger-remarks', entry.id],
    queryFn: async () => (await api.get<Remark[]>('/ledger/remarks', { params: { entryId: entry.id } })).data,
  });

  const add = useMutation({
    mutationFn: async () =>
      (await api.post('/ledger/remarks', { entryId: entry.id, merchantId: entry.merchantId, remark: text.trim() })).data,
    onSuccess: () => {
      setText('');
      qc.invalidateQueries({ queryKey: ['ledger-remarks', entry.id] });
      onDone();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not add remark'),
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-16 z-50">
      <div className="bg-white rounded-lg w-[520px] max-w-[95vw] max-h-[85vh] flex flex-col">
        <div className="bg-emerald-700 text-white px-5 py-3 flex items-center justify-between">
          <div className="font-semibold">Remarks · {entry.typeLabel} {entry.refNo}</div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3 text-sm overflow-y-auto flex-1">
          {isLoading ? (
            <div className="text-gray-400 text-center py-6">Loading…</div>
          ) : (remarks?.length ?? 0) === 0 ? (
            <div className="text-gray-400 text-center py-6">No remarks yet.</div>
          ) : (
            <ul className="space-y-2">
              {remarks!.map((r) => (
                <li key={r.id} className="border rounded p-2.5 bg-slate-50">
                  <div className="text-slate-800 whitespace-pre-wrap">{r.remark}</div>
                  <div className="text-[11px] text-gray-500 mt-1">
                    {r.by ? `${r.by} · ` : ''}{new Date(r.at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        {canAdd && (
          <div className="px-5 py-3 border-t space-y-2">
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Add a remark…"
              className="w-full border rounded px-2 py-1.5 text-sm" />
            <div className="flex justify-end">
              <button onClick={() => add.mutate()} disabled={!text.trim() || add.isPending}
                className="bg-emerald-600 text-white px-4 py-1.5 rounded text-sm disabled:opacity-50">
                {add.isPending ? 'Adding…' : 'Add Remark'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, note, tone }: { label: string; value?: number; note?: string; tone?: 'debit' | 'credit' | 'balance' }) {
  const color =
    tone === 'debit' ? 'text-blue-700' : tone === 'credit' ? 'text-emerald-700' : tone === 'balance' ? 'text-gray-900' : 'text-gray-700';
  return (
    <div className="bg-white border rounded p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className={`text-lg font-semibold ${color}`}>
        {note ? <span className="text-sm text-gray-400 font-normal">{note}</span> : `₹ ${money(value ?? 0)}`}
      </div>
    </div>
  );
}
